import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { loginApi, registerApi } from '../api/authApi';
import { getProfileApi } from '../api/userApi';
import { TOKEN_KEY, REFRESH_TOKEN_KEY, USER_KEY, WALLET_KEY, LEGACY_KEYS } from '../constants/config';
import { saveWallet } from '../utils/wallet';

const AuthContext = createContext(null);

export function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(WALLET_KEY);
  LEGACY_KEYS.forEach((k) => localStorage.removeItem(k));
}

// localStorage me sirf userId rehta hai; baaki user info profile API se aati hai
function readUser() {
  const id = localStorage.getItem(USER_KEY);
  return id ? { userId: id } : null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(readUser);
  const [accessToken, setAccessToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = useCallback(() => {
    clearSession();
    setUser(null);
    setAccessToken(null);
  }, []);

  // token ho to profile se username + wallet lao
  useEffect(() => {
    if (!accessToken) return undefined;
    let alive = true;
    getProfileApi()
      .then((res) => {
        const d = res?.data?.data;
        if (!alive || !d) return;
        if (d.user) {
          setUser(d.user);
          localStorage.setItem(USER_KEY, String(d.user.userId || d.user._id));
        }
        saveWallet(d.wallet);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [accessToken]);

  useEffect(() => {
    const onUnauthorized = () => logout();
    const onStorage = (event) => {
      if (!event.key || [TOKEN_KEY, USER_KEY, REFRESH_TOKEN_KEY].includes(event.key)) {
        setAccessToken(localStorage.getItem(TOKEN_KEY));
        setUser(readUser());
      }
    };
    window.addEventListener('auth:unauthorized', onUnauthorized);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('auth:unauthorized', onUnauthorized);
      window.removeEventListener('storage', onStorage);
    };
  }, [logout]);

  const login = useCallback(async ({ identifier, password }) => {
    const response = await loginApi({ identifier: identifier.trim(), password });
    const body = response.data;
    if (!body?.success || !body?.data?.accessToken || !body?.data?.user) {
      throw new Error(body?.message || 'Unable to sign in. Please try again.');
    }
    localStorage.setItem(TOKEN_KEY, body.data.accessToken);
    if (body.data.refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, body.data.refreshToken);
    else localStorage.removeItem(REFRESH_TOKEN_KEY);
    LEGACY_KEYS.forEach((k) => localStorage.removeItem(k));
    localStorage.setItem(USER_KEY, String(body.data.user.userId || body.data.user._id));
    saveWallet(body.data.wallet);
    setUser(body.data.user);
    setAccessToken(body.data.accessToken);
    return body;
  }, []);

  // Registration response wasn't supplied; don't assume it contains tokens.
  const register = useCallback(async ({ username, mobile, password }) => {
    const response = await registerApi({ username: username.trim(), mobile: mobile.trim(), password });
    const body = response.data;
    if (body?.success === false) throw new Error(body.message || 'Registration failed.');
    return body;
  }, []);

  const value = useMemo(() => ({ user, isAuthenticated: Boolean(accessToken), login, register, logout }), [user, accessToken, login, register, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
